import axios from 'axios'
import {markRaw} from 'vue'

/**
 * Reporting store.
 *
 * The catalog (which reports exist, how they are grouped, what parameters each
 * takes) comes from the server (GET /admin/report/catalog) and is never duplicated
 * here. The server filters it by the caller's permissions, so a report this user may
 * not run is never even named. Adding a report to the API makes it appear in this UI
 * with no frontend change.
 *
 * On the dedicated axios instance: this follows barcodeStoreVuex.js, and it is not
 * only style. Interceptors registered on the default axios export do NOT apply to
 * instances made with axios.create(), which is what we want here: the global one in
 * store/index.js ends its error branch with `return error` instead of
 * `return Promise.reject(error)`, so failed requests RESOLVE with an Error object.
 * Any caller doing `.then(r => r.data)` gets undefined and no .catch ever fires. On
 * our own instance we handle failures properly.
 */

const API_BASE_URL = process.env.VUE_APP_API_SERVER || 'http://127.0.0.1:8000/api'

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {'Content-Type': 'application/json'},
})

api.interceptors.response.use(
    response => {
        // 2FA pending comes back as 206 carrying a SUCCESS-shaped body, so it has to
        // be checked explicitly: response.ok / a 2xx check would treat "mfa
        // required" as report data. Matches the global interceptor's behavior.
        if (response.status === 206 && response.data !== undefined && !response.data.mfapass) {
            location.href = '/mfacode'
        }
        return response
    },
    error => {
        // error.response is undefined on a network or CORS failure: the global
        // interceptor reads error.response.status unguarded and throws there.
        const status = error.response ? error.response.status : null

        if (status === 401) {
            localStorage.clear()
            location.href = '/login'
        }

        return Promise.reject(error)
    }
)

const authHeader = getters => ({
    Authorization: 'Bearer ' + getters['userAccess/accessToken'],
})

// Lookup lists for report filters (GET /admin/report/options/{source}). A whole
// list is fetched once per session and shared by every param naming it; the
// promise is kept, not just the result, so two pickers mounting together still
// make one request. Search results are kept for a minute per query, which is
// enough to absorb retyping without serving a stale list for long.
const optionLists = {}
const searchResults = {}
const SEARCH_TTL_MS = 60 * 1000

export default {
    namespaced: true,
    state: {
        // Report groups as the server returned them, already permission-filtered.
        catalog: [],
        catalogLoaded: false,
        // The catalog response's meta: {company, user: {location, salesCode}}.
        // FrameTracker prints Sys.Company on its page headers and presets some
        // forms to the operator's Sys.Loc; the API reads both for the caller.
        catalogMeta: {},
        rows: [],
        meta: null,
        error: null,
    },
    getters: {
        getCatalog(state) {
            return state.catalog
        },
        catalogLoaded(state) {
            return state.catalogLoaded
        },
        getCatalogMeta(state) {
            return state.catalogMeta
        },
        getRows(state) {
            return state.rows
        },
        getMeta(state) {
            return state.meta
        },
        getError(state) {
            return state.error
        },
        /**
         * Flat slug -> definition lookup, so a runner page can resolve its report
         * without knowing which group it lives in.
         */
        definitionBySlug(state) {
            const map = {}
            state.catalog.forEach(group => {
                (group.reports || []).forEach(report => {
                    map[report.slug] = {...report, groupLabel: group.label}
                })
            })
            return map
        },
    },
    actions: {
        /**
         * Loads the catalog once per session. Pass {force: true} after a permission
         * change to pick it up without a reload.
         */
        fetchCatalog({commit, state}, {force = false} = {}) {
            if (state.catalogLoaded && !force) {
                return Promise.resolve(state.catalog)
            }

            return api
                .get('/admin/report/catalog', {headers: authHeader(this.getters)})
                .then(response => {
                    commit('setCatalog', response.data.data)
                    commit('setCatalogMeta', response.data.meta)
                    return response.data.data
                })
                .catch(error => {
                    commit('setError', readError(error, 'Could not load the report list.'))
                    throw error
                })
        },

        /**
         * Runs one report.
         *
         * Note `params`, not `data`: axios drops a request body on GET, which is why
         * the previous version of this module silently sent no filters at all.
         */
        runReport({commit}, {slug, params, endpoint = null}) {
            commit('setError', null)

            // A report predating this catalog keeps its own route and response shape
            // (the catalog reports it as `endpoint`). Cataloguing such a report must
            // not break it, so it is called where it actually lives.
            const url = endpoint || '/admin/report/run/' + slug

            return api
                .get(url, {
                    headers: authHeader(this.getters),
                    params: params,
                })
                .then(response => {
                    commit('setResult', response.data)
                    return response.data
                })
                .catch(error => {
                    const status = error.response ? error.response.status : null

                    // 403 is a real outcome here, not an edge case: report access is
                    // per group. The global interceptor ignores 403 entirely, which
                    // is one reason this module uses its own instance.
                    let message = readError(error, 'The report could not be run.')
                    if (status === 403) {
                        message = readError(error, 'You do not have access to this report.')
                    } else if (status === 501) {
                        message = readError(error, 'This report has not been migrated yet.')
                    }

                    commit('setError', message)
                    commit('setResult', {data: [], meta: null})
                    throw error
                })
        },

        /**
         * Options for one lookup source, as [{value, label}].
         *
         * `q` searches a source too large to load whole (accounts, products);
         * `value` resolves one known value to its label. Without either, the whole
         * list is returned and cached (`fresh` reloads it). A failure only costs the picker its list:
         * it is logged and resolves to [], so the form still runs.
         */
        fetchOptions(context, {source, q = null, value = null, fresh = false}) {
            const params = {}
            if (q !== null && q !== '') params.q = q
            if (value !== null && value !== '') params.value = value

            const whole = Object.keys(params).length === 0
            const key = source + '\u0000' + (params.q || '') + '\u0000' + (params.value || '')

            // `fresh` re-reads a whole list (live readouts such as # of Records).
            if (whole && fresh) delete optionLists[source]
            if (whole && optionLists[source]) return optionLists[source]
            if (!whole && searchResults[key] && searchResults[key].expires > Date.now()) {
                return searchResults[key].promise
            }

            const promise = api
                .get('/admin/report/options/' + encodeURIComponent(source), {
                    headers: authHeader(this.getters),
                    params: params,
                })
                .then(response => (response.data && response.data.data) || [])
                .catch(error => {
                    console.error('Report options for "' + source + '" could not be loaded.', error)
                    // Forget the failure, so the next form to open tries again.
                    if (whole) delete optionLists[source]
                    else delete searchResults[key]
                    return []
                })

            if (whole) optionLists[source] = promise
            else searchResults[key] = {promise, expires: Date.now() + SEARCH_TTL_MS}

            return promise
        },

        clearResult({commit}) {
            commit('setResult', {data: [], meta: null})
            commit('setError', null)
        },
    },
    mutations: {
        setCatalog(state, catalog) {
            state.catalog = catalog || []
            state.catalogLoaded = true
        },
        setCatalogMeta(state, meta) {
            state.catalogMeta = meta || {}
        },
        setResult(state, payload) {
            // markRaw: a result is read-only, and making tens of thousands of rows
            // reactive (Product Listing returns 48,000+) costs seconds for nothing.
            state.rows = markRaw((payload && payload.data) || [])
            state.meta = (payload && payload.meta) || null
        },
        setError(state, error) {
            state.error = error
        },
    },
}

/**
 * The API returns a `message` on its error responses; fall back to a caller-supplied
 * sentence rather than surfacing a raw axios string to a user.
 */
function readError(error, fallback) {
    if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message
    }
    return fallback
}
